import React from "react";

import { File, Pencil, Trash2, UserRound } from "lucide-react";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  countColumn,
  dateColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { eduEditPath } from "../../routes/routes";

import type { TFunction } from "i18next";

import type { DataTableColumnDef } from "../../dataTable/dataTable.types";
import type { EduInstitutionRow } from "../edu.types";

const EDU_TYPE_LIST = ["cas", "liblynx", "openid"] as const;

interface EduInstitutionColumnsOptions {
  t: TFunction;
  projectSlug: string;
  onEdit: (institution: EduInstitutionRow) => void;
  onDelete: (institution: EduInstitutionRow) => void;
}

export function eduInstitutionColumns({
  t,
  projectSlug,
  onEdit,
  onDelete,
}: EduInstitutionColumnsOptions): DataTableColumnDef<EduInstitutionRow>[] {
  return [
    statusColumn<EduInstitutionRow>("edu_status", t("status"), [
      { value: "active", label: t("common:active"), tone: "success" },
      { value: "stop", label: t("common:stop"), tone: "error" },
    ]),
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("title")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell href={eduEditPath(projectSlug, row.original.uuid)}>
          {row.original.label}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "user_edu",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("eduType")} />
      ),
      enableSorting: false,
      meta: {
        label: t("eduType"),
        searchable: true,
        width: "16%",
        minWidth: 135,
        filterOptions: EDU_TYPE_LIST.map((type) => ({
          value: type,
          label: t(type),
        })),
      },
      cell: ({ row }) => String(t(row.original.user_edu)),
    },
    countColumn<EduInstitutionRow>(
      "max_connexion",
      t("max_connexion"),
      <File />,
      { width: "14%", minWidth: 170 },
    ),
    countColumn<EduInstitutionRow>("students", t("students"), <UserRound />, {
      width: "12%",
      minWidth: 140,
    }),
    dateColumn<EduInstitutionRow>("updated_at", t("update_date")),
    actionsColumn<EduInstitutionRow>(
      (institution) => [
        {
          label: t("editor-content:edit_action"),
          icon: <Pencil />,
          onClick: () => onEdit(institution),
        },
        {
          label: t("editor-content:delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(institution),
          confirm: {
            title: t("deleteEduTitle"),
            description: t("deleteEduBody", { name: institution.label }),
            confirmLabel: t("editor-content:delete"),
          },
        },
      ],
      (institution) => institution.label,
    ),
  ];
}
